import { Html } from '@react-three/drei';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Box3, Camera, Object3D, Vector3 } from 'three';
import styled from 'styled-components';

// Types
import { Entity, LabelMode, TwinEntityType } from '@repo/frontend-types';

// Context
import {
  DataContext,
  useDataContext,
  useEventContext,
  FilterContext,
  useModeContext,
  useFilterContext,
  useTwinConfigStore,
} from '@repo/front-end-state';

// Utils
import {
  resolveLabelRows,
  adjustColorByDeltaE,
  normaliseHeadcountValue,
} from '@repo/front-end-utils';
import { useHeroIndicator, TLCard, Labels, TrackedHeroDonut, UntrackedHeroDonut } from '@repo/ui';
import { Label3DPointer, Label3DPointerInfo } from './Label3DPointer';

interface Label3DProps {
  className?: string;
  entity: Entity;
  boundingBox: Box3;
  entityType: TwinEntityType;
  labelMode: LabelMode;
  visible: boolean;
  isSelected: boolean;
  lineage?: Entity[];
}

const MAX_MINWIDTH = 200;

const LOADING_DELAY_STYLE = {
  hidden: { opacity: 0, visibility: 'hidden' as const },
  visible: { opacity: 1, visibility: 'visible' as const },
};

const projectObjectToScreen = (
  obj: Object3D,
  camera: Camera,
  size: { width: number; height: number },
) => {
  const worldPosition = new Vector3();
  obj.getWorldPosition(worldPosition);
  worldPosition.project(camera);

  const widthHalf = size.width / 2;
  const heightHalf = size.height / 2;

  const x = worldPosition.x * widthHalf + widthHalf;
  const y = -(worldPosition.y * heightHalf) + heightHalf;

  return [x, y];
};

const Label3D_ = ({
  className,
  entity,
  boundingBox,
  entityType,
  labelMode,
  visible,
  isSelected,
  lineage,
}: Label3DProps) => {
  const [styles] = useState({
    fontSize: 14,
    minWidth: MAX_MINWIDTH,
  });
  const [eventListenersAdded, setEventListenersAdded] = useState(false);
  const pointerRef = useRef<Label3DPointerInfo | null>(null);
  const htmlRef = useRef<HTMLDivElement | null>(null);
  const [htmlSize, setHtmlSize] = useState({
    width: 0,
    height: 0,
  });
  const [pointerPos, setPointerPos] = useState<Vector3>(new Vector3());
  const [hover, setHover] = useState(false);
  const [menuHovered] = useState(false);
  const [isLoadingDelayComplete, setIsLoadingDelayComplete] = useState(false);
  const loadingDelayRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // Prevent hover state when item is selected to avoid conflicts
    setHover(isSelected ? false : visible);
  }, [visible, isSelected]);

  useEffect(() => {
    if (loadingDelayRef.current) {
      clearTimeout(loadingDelayRef.current);
      loadingDelayRef.current = null;
    }

    if (visible && isSelected) {
      setIsLoadingDelayComplete(false);
      loadingDelayRef.current = setTimeout(() => {
        setIsLoadingDelayComplete(true);
      }, 5);
    } else if (visible && !isSelected) {
      setIsLoadingDelayComplete(true);
    } else {
      setIsLoadingDelayComplete(false);
    }

    return () => {
      if (loadingDelayRef.current) {
        clearTimeout(loadingDelayRef.current);
      }
    };
  }, [visible, isSelected]);

  const setHtmlRef = useCallback((node: HTMLDivElement | null) => {
    if (node) {
      htmlRef.current = node;
      // Single size calculation to avoid multiple calls
      const { width, height } = node.getBoundingClientRect();
      if (width > 0 && height > 0) {
        setHtmlSize({ width, height });
      }
    }
  }, []);

  useLayoutEffect(() => {
    const element = htmlRef.current;
    if (!element) return;

    const resizeObserver = new ResizeObserver(entries => {
      for (let entry of entries) {
        const { width, height } = entry.contentRect;
        // Only update size if the label is visible and not causing conflicts
        if (visible && !isSelected) {
          setHtmlSize({ width, height });
        }
      }
    });

    resizeObserver.observe(element);

    return () => {
      resizeObserver.unobserve(element);
    };
  }, [visible, isSelected]);

  useEffect(() => {
    if (htmlRef.current && visible) {
      const { width, height } = htmlRef.current.getBoundingClientRect();
      setHtmlSize({ width, height });
    }
  }, [visible]);

  const getLabelPos = useCallback(
    (obj: Object3D, camera: Camera, windowInnerSize: { width: number; height: number }) => {
      if (pointerRef.current && htmlSize) {
        const pointerBaseScreenPos = projectObjectToScreen(obj, camera, windowInnerSize);

        const verticalOffset = labelMode === LabelMode.FULL ? 125 : 75;

        let lp = [
          pointerBaseScreenPos[0] - htmlSize.width / 2,
          pointerBaseScreenPos[1] - htmlSize.height - verticalOffset,
        ];

        return lp;
      } else {
        return [window.innerWidth, window.innerHeight];
      }
    },
    [htmlSize, labelMode],
  );

  const storePointerRef = useCallback((pr: Label3DPointerInfo | null) => {
    if (!pointerRef.current && pr) {
      pointerRef.current = pr;
    }
  }, []);

  useEffect(() => {
    const handler = () => {
      // Only update size if the label is actually visible and not causing conflicts
      if (htmlRef.current && visible && !isSelected) {
        const { width, height } = htmlRef.current.getBoundingClientRect();
        setHtmlSize({ width, height });
      }
    };

    if (!eventListenersAdded) {
      window.addEventListener('pointermove', handler);
      window.addEventListener('wheel', handler);
      setEventListenersAdded(true);
    }

    return () => {
      window.removeEventListener('pointermove', handler);
      window.removeEventListener('wheel', handler);
    };
  }, [eventListenersAdded, visible, isSelected]);

  const { appMode } = useModeContext();
  const { heroMetric } = useFilterContext();
  const { data } = useDataContext();
  useEventContext();

  // AdminDB-configured label rows for this twin, straight off the `config-by-bid` wire
  // (label-management epic, ledger rows 14-15). THE ONLY LABEL SOURCE — the compiled label sets
  // that used to back a fallback are gone. `undefined` and `[]` still mean DIFFERENT things (see
  // TwinConfigStore's `labels` doc comment): no answer yet versus an authoritative none.
  const wireLabels = useTwinConfigStore(state => state.labels);

  // Use the hero indicator hook for cleaner logic
  const { indicator: hookIndicator, presentation } = useHeroIndicator(entity);

  let fullLabels: React.JSX.Element | null = useMemo(() => {
    if (labelMode !== LabelMode.FULL) {
      return null;
    }

    // ⚠️ `Array.isArray`, not truthiness and not `wireLabels?.length`. `TwinConfigStore` carries
    // what `config-by-bid` sent VERBATIM and by design (FR1), so `undefined` (no answer yet, or a
    // middleware serving no `labels` key) and a malformed non-array both reach here.
    //
    // WHAT THIS GUARD IS FOR: choosing to render NOTHING over rendering an empty label list. It is
    // the BRACES, not the belt — `resolveLabelRows` has its own `Array.isArray` early return, so
    // deleting this line would not throw, it would render an empty `<ul>` on every twin whose
    // labels have not arrived. Keep both: this one owns the rendering decision, the resolver's
    // owns its own totality contract. (Do not "simplify" either on the grounds that the other
    // exists.)
    //
    // An empty ARRAY is NOT this case. It takes the branch below and authoritatively renders no
    // rows, which is what a template deliberately configured with none must do — collapsing the
    // two with `?.length` would make "configured empty" indistinguishable from "no answer yet".
    if (!Array.isArray(wireLabels)) {
      return null;
    }

    const resolvedRows = resolveLabelRows(wireLabels, { entityType, appMode, entity, data });
    return (
      <Labels
        entity={entity}
        cssProperties={{ ...styles, marginTop: 5 }}
        resolvedRows={resolvedRows}
      />
    );
    // `data` is a dependency and was NOT one before the wire path: `LabelValue` used to subscribe
    // to the data context itself, one instance per row. Values are computed here now, so without
    // `data` the labels would freeze at their first render.
  }, [appMode, data, entity, entityType, labelMode, styles, wireLabels]);

  const Y_OFFSET = 1;

  useLayoutEffect(() => {
    const getMaxHeight = () => {
      let maxHeight = boundingBox.max.y;
      const baseHeight = 1;
      const depth = lineage?.length ?? 0;
      const depthAdjustment = depth * 0.05;
      maxHeight = Math.max(maxHeight, baseHeight - depthAdjustment);
      return maxHeight;
    };

    const maxY = getMaxHeight();
    const newPosition = new Vector3(
      (boundingBox.min.x + boundingBox.max.x) / 2,
      maxY + Y_OFFSET,
      (boundingBox.min.z + boundingBox.max.z) / 2,
    );

    if (!pointerPos.equals(newPosition)) {
      setPointerPos(newPosition);
    }
  }, [boundingBox, lineage, entityType, pointerPos]);

  // Use hook indicator as primary source, with fallback for backwards compatibility
  const indicator = hookIndicator;

  // Headcount→%-of-capacity normalisation for the donut fill fraction (doughnutFillValue). This
  // is the ONE live use of the normalisation — it is NOT a colour input (colour comes from the raw
  // indicator value; see resolveEntityBehaviourColor). Shared with Box via @repo/front-end-utils.
  const colorCalculationValue = useMemo(
    () => normaliseHeadcountValue(heroMetric?.name, entity.capacity, indicator?.value ?? 0),
    [indicator?.value, heroMetric?.name, entity.capacity],
  );

  // Simplified color indicator using hook data
  const colorIndicator = useMemo(() => {
    if (!indicator) return null;

    const deltaE = -5.29;
    const indicatorColorInner = adjustColorByDeltaE(indicator.colors.bgColor, deltaE);
    return {
      indicatorColorOuter: indicator.colors.bgColor,
      indicatorColorInner,
    };
  }, [indicator]);

  const indicatorColorOuter = colorIndicator?.indicatorColorOuter;
  const indicatorColorInner = colorIndicator?.indicatorColorInner;

  // For the doughnut fill-up, we need the percentage (0-100)
  const doughnutFillValue = colorCalculationValue;

  // For the text display, we need the original indicator value (raw count or percentage)
  const textDisplayValue = indicator?.value || 0;

  // Determine if we should show the % sign
  // For headcount mode in historical mode, don't show %
  const shouldShowPercent = useMemo(() => {
    const metricType = heroMetric?.name?.toLowerCase() || '';

    if (metricType.includes('headcount') || metricType.includes('count')) {
      // For headcount mode, don't show % sign
      return false;
    }
    // For usage mode, use the presentation setting
    return presentation?.showUnit;
  }, [heroMetric?.name, presentation?.showUnit]);

  return (
    <>
      <Label3DPointer
        entity={entity}
        tipPosition={pointerPos}
        scale={0.003}
        // Selected items can NEVER have hover state
        isHovered={!isSelected && (hover || menuHovered)}
        isSelected={isSelected}
        edgeColor={entity.tracked ? indicatorColorOuter : '#808080'}
        ref={pr => {
          if (pr) {
            storePointerRef(pr);
          }
        }}
      >
        <DataContext.Consumer>
          {dataValue => (
            <FilterContext.Consumer>
              {filterValue => (
                <Html
                  ref={setHtmlRef}
                  key={`${entity.id}-${isSelected ? 'selected' : 'hover'}`}
                  className={`${className} label-3d${labelMode === LabelMode.BRIEF ? ' brief' : ''}`}
                  calculatePosition={(el, cam, sz) => {
                    const pointerEl = el.parent;

                    if (pointerEl?.userData?.marker === `Label3DPointer`) {
                      // Only calculate position if the label should be visible
                      if (visible && isLoadingDelayComplete) {
                        let pos = getLabelPos(pointerEl, cam, sz);
                        return pos;
                      } else {
                        return [-10000, -10000];
                      }
                    } else {
                      return [-10000, -10000];
                    }
                  }}
                >
                  <div
                    style={
                      isLoadingDelayComplete
                        ? LOADING_DELAY_STYLE.visible
                        : LOADING_DELAY_STYLE.hidden
                    }
                  >
                    <TLCard size={'small'} className="testing">
                      <DataContext.Provider value={dataValue}>
                        <FilterContext.Provider value={filterValue}>
                          <div className="hero-donut-wrapper">
                            {entity.tracked && (
                              <TrackedHeroDonut
                                zeroToOneValue={doughnutFillValue / 100}
                                text={textDisplayValue.toFixed(
                                  presentation?.showDecimalPlace ? 1 : 0,
                                )}
                                outerColor={indicatorColorOuter!}
                                innerColor={indicatorColorInner!}
                                percent={shouldShowPercent}
                                indicatorValue={doughnutFillValue}
                                className="donut"
                              />
                            )}
                            {!entity.tracked && (
                              <UntrackedHeroDonut
                                className="donut"
                                isUtilization={presentation?.showUnit}
                              />
                            )}
                            <div className="hero-name">{entity.name}</div>
                          </div>
                          {fullLabels}
                        </FilterContext.Provider>
                      </DataContext.Provider>
                    </TLCard>
                  </div>
                </Html>
              )}
            </FilterContext.Consumer>
          )}
        </DataContext.Consumer>
      </Label3DPointer>
    </>
  );
};

export const Label3D = styled(Label3D_)`
  display: flex;
  flex-direction: row;
  min-width: 200px;
  z-index: 9999;
  color: #242424;
  font-family: 'Inter', sans-serif;
  /* Was Tailwind whitespace-nowrap + pointer-events-none on the label root. */
  white-space: nowrap;
  pointer-events: none;

  .hero-donut-wrapper *,
  .hero-name * {
    color: #242424 !important;
  }

  &.brief {
    height: 76px !important;
    z-index: 1000;
    border-top-left-radius: 14px;
    border-bottom-left-radius: 14px;
  }

  .fui-Card {
    overflow: visible;
  }

  .hero-donut-wrapper {
    display: flex;
    color: #242424;
  }

  .hero-name {
    font-family: 'Inter', sans-serif !important;
    font-weight: 600 !important;
    font-size: 1.3em;
    display: flex;
    flex-direction: column;
    margin: 4px 8px 34px 8px;
    justify-content: center;
    overflow-wrap: break-word;
    word-wrap: break-word;
    white-space: normal;
    max-width: 100%;
    min-width: 165px;
    color: #242424 !important;
  }
`;
